import { useEffect, useMemo, useState } from 'react';

import type { Order } from '#domain/Order.ts';

import { anchorTourEndpointsAtEdges, getStopsFromOrders, type TourStop } from './TourStop.ts';

export const useHandleTourStops = (isOpen: boolean, orders: Order[], initialStops?: TourStop[]) => {
    const [isMisplaced, setIsMisplaced] = useState(false);
    const [misplacedStopIds, setMisplacedStopIds] = useState<string[]>([]);
    const [sortedTourStops, setSortedTourStops] = useState<TourStop[]>([]);

    const orderIdsKey = useMemo(() => orders.map(order => order.id).join('|'), [orders]);

    useEffect(() => {
        if (isOpen) {
            setSortedTourStops(anchorTourEndpointsAtEdges(initialStops ?? getStopsFromOrders(orders)));
        }
    }, [initialStops, isOpen, orderIdsKey]);

    useEffect(() => {
        if (isOpen) {
            const misplacedIds = findMisplacedStopIds(sortedTourStops);
            setMisplacedStopIds(misplacedIds);
            setIsMisplaced(misplacedIds.length > 0);
        }
    }, [isOpen, sortedTourStops]);

    return {
        sortableTourStops: sortedTourStops,
        setSortableTourStops: setSortedTourStops,
        isMisplaced,
        misplacedStopIds,
    };
};

export const findMisplacedStopIds = (stops: TourStop[]): string[] => {
    const misplacedIds = new Set<string>();
    const starts = stops.filter(stop => stop.category === 'tour-start');
    const destinations = stops.filter(stop => stop.category === 'tour-end');
    if (starts.length > 1) {
        for (const stop of starts) {
            misplacedIds.add(stop.id);
        }
    }
    if (destinations.length > 1) {
        for (const stop of destinations) {
            misplacedIds.add(stop.id);
        }
    }
    stops.forEach((stop, index) => {
        if (stop.category === 'tour-start' && index !== 0) {
            misplacedIds.add(stop.id);
        }
        if (stop.category === 'tour-end' && index !== stops.length - 1) {
            misplacedIds.add(stop.id);
        }
        if (stop.type === 'unload') {
            const loadIndex = stops.findIndex(other => other.group === stop.group && other.type === 'load');
            if (loadIndex !== -1 && index < loadIndex) {
                misplacedIds.add(stop.id);
            }
        }
    });
    return [...misplacedIds];
};
